import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { HrOrgStructure } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function HrOrgStructurePage() {
  const [structure, setStructure] = useState<HrOrgStructure | null>(null);
  const [deptName, setDeptName] = useState('');
  const [deptCode, setDeptCode] = useState('');
  const [desName, setDesName] = useState('');
  const [desCode, setDesCode] = useState('');
  const [buName, setBuName] = useState('');
  const [buCode, setBuCode] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStructure(await hrApi.getOrgStructure());
    } catch (err) {
      showApiError(err, 'Failed to load org structure');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createDepartment = async () => {
    try {
      await hrApi.createDepartment({ name: deptName, code: deptCode });
      notify.success('Department created');
      setDeptName('');
      setDeptCode('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create department');
    }
  };

  const createDesignation = async () => {
    try {
      await hrApi.createDesignation({ name: desName, code: desCode });
      notify.success('Designation created');
      setDesName('');
      setDesCode('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create designation');
    }
  };

  const createBusinessUnit = async () => {
    try {
      await hrApi.createBusinessUnit({ name: buName, code: buCode });
      notify.success('Business unit created');
      setBuName('');
      setBuCode('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create business unit');
    }
  };

  return (
    <div>
      <PageHeader
        title="Organization structure"
        description="Departments, designations, business units, and reporting hierarchy"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Org structure' }]}
      />
      {loading && <p className="mb-4 text-sm text-slate-500">Loading…</p>}
      <div className="grid gap-4 lg:grid-cols-3">
        <section className="card space-y-3 p-4">
          <h3 className="font-semibold">Departments</h3>
          <Input label="Name" value={deptName} onChange={(e) => setDeptName(e.target.value)} />
          <Input label="Code" value={deptCode} onChange={(e) => setDeptCode(e.target.value)} />
          <Button onClick={() => void createDepartment()}>Add department</Button>
          <ul className="text-sm">
            {(structure?.departments ?? []).map((d) => (
              <li key={d.id}>
                {d.name} ({d.code})
              </li>
            ))}
          </ul>
        </section>
        <section className="card space-y-3 p-4">
          <h3 className="font-semibold">Designations</h3>
          <Input label="Name" value={desName} onChange={(e) => setDesName(e.target.value)} />
          <Input label="Code" value={desCode} onChange={(e) => setDesCode(e.target.value)} />
          <Button onClick={() => void createDesignation()}>Add designation</Button>
          <ul className="text-sm">
            {(structure?.designations ?? []).map((d) => (
              <li key={d.id}>
                {d.name} ({d.code})
              </li>
            ))}
          </ul>
        </section>
        <section className="card space-y-3 p-4">
          <h3 className="font-semibold">Business units</h3>
          <Input label="Name" value={buName} onChange={(e) => setBuName(e.target.value)} />
          <Input label="Code" value={buCode} onChange={(e) => setBuCode(e.target.value)} />
          <Button onClick={() => void createBusinessUnit()}>Add business unit</Button>
          <ul className="text-sm">
            {(structure?.businessUnits ?? []).map((b) => (
              <li key={b.id}>
                {b.name} ({b.code})
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
